import { CropYear } from "../cropYear"
import { ICropping } from "../fields"
import { selectMany } from "../utils/array"
import { checkDateIntersection } from "../utils/date"
import { isArray, isDate } from "../utils/utils"
import { getFieldHistory } from "./autohistory"
import { getPerennialDatesInYear } from "./perennial"

export function getCroppingDatesInYear(crop: ICropping) {

    return crop.Crop && crop.Crop.Perennial
        ? getPerennialDatesInYear(crop)
        : {
            start: crop.SowingDate || crop.StartDate,
            end: (crop.Destroyed ? crop.DestructionDate : crop.HarvestDate)
                || (crop.HarvestDate || crop.DestructionDate)
                || crop.EndDate
                || CropYear.getYearEnd(crop.YearId)
        }

}

export function getCroppingSiblings(crop: ICropping, includeSelf: boolean = true): ICropping[] {

    if (crop) {

        var previous = crop.PreviousCroppings || [];
        var siblings: ICropping[];

        if (!previous.length) {
            siblings = crop.Field.Cropping.filter(c => !c.PreviousCroppings.length);
        }
        else {
            siblings = selectMany(previous.map(p => p.NextCroppings));
        }

        if (!includeSelf) {
            siblings = siblings.filter(s => s.CroppingId != crop.CroppingId);
        }

        return siblings;

    }

}

export function getConcurrentCrops(
    target: ICropping,
    options?: {
        cropping?: ICropping[]
        dates?: { start: Date, end: Date },
        filterRelated?: boolean,
        year?: number
    }) {

    const {
        cropping = target.Field.Cropping,
        dates = getCroppingDatesInYear(target),
        filterRelated = true,
        year
    } = options || {}

    const { start, end } = dates

    if (!isDate(start)) return []
    if (!isDate(end)) return []

    const checkDates = (c: ICropping) => {
        const { start: otherStart, end: otherEnd } = getCroppingDatesInYear(c)
        return checkDateIntersection(start, end, otherStart, otherEnd, true)
    }

    const filter = (c: ICropping) => (c.CroppingId !== target.CroppingId) && checkDates(c)

    const cc1 = cropping
        .filter(filter)
        .sort((a, b) => b.Area - a.Area)


    const cc2 = filterRelated
        ? cc1.filter((c, index) => {
            if (!index) return true
            return cc1.slice(0, index - 1).some(p => checkNext(p, c))
        })
        : cc1

    const cc3 = year
        ? cc2.filter(c => c.YearId == year)
        : cc2

    return cc3

    function checkNext(c: ICropping, i: ICropping) {
        const check = (c: ICropping) => (c == i) || (isArray(c.NextCroppings) && c.NextCroppings.some(check))
        return check(c)
    }

}

export function getEarliestNextCrop(c: ICropping): ICropping {

    const next = c.NextCroppings

    if (next && next.length) {
        return next.reduce((d1, d2) => d1.StartDate < d2.StartDate ? d1 : d2)
    }

}

export function getLatestPreviousCrop(c: ICropping): ICropping {

    const previous = c.PreviousCroppings

    if (previous && previous.length) {
        return previous.reduce((d1, d2) => d1.EndDate < d2.EndDate ? d2 : d1)
    }

}

export function getStdFieldHistory(cropping: ICropping[], field: { fieldArea: number, fieldName: string }) {

    const selector = (c: ICropping) => {

        const { start: startDate, end: endDate } = getCroppingDatesInYear(c)

        return {

            sourceId: c.CroppingId,

            year: c.YearId,
            cropId: c.CropId,
            perennial: c.Crop && c.Crop.Perennial,
            area: c.Area,
            destroyed: !!c.DestructionDate,
            startDate,
            endDate,

        }

    }

    return getFieldHistory<ICropping>(cropping, field, selector)
}
